import { FormSelect, Input } from '@/components/atoms';
import { FormField } from '@/components/molecules';
import { cities, countries, nigerianStates } from '@/lib/constants';
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldValues,
  type UseFormRegister,
  type UseFormWatch,
} from 'react-hook-form';

interface BusinessAddressFieldsProps {
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  disabled?: boolean;
  className?: string;
}

export function BusinessAddressFields({
  register,
  control,
  errors,
  watch,
  disabled = false,
  className = 'grid grid-cols-1 md:grid-cols-2 gap-4',
}: BusinessAddressFieldsProps) {
  const selectedState = watch('state') as string;
  const selectedLga = watch('lga') as string;
  const selectedCountry = watch('country') as string;

  return (
    <div className={className}>
      <FormField
        label="Business Address"
        className="md:col-span-2"
        error={errors.businessAddress?.message as string | undefined}
        required
      >
        <Input
          placeholder="Enter Address"
          {...register('businessAddress')}
          disabled={disabled}
          readOnly={disabled}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Enter the official address used to register your business with CAC or
          other regulatory bodies. This should match your registration and
          compliance documents.
        </p>
      </FormField>

      <FormField
        label="State"
        error={errors.state?.message as string | undefined}
        required
      >
        <Controller
          name="state"
          control={control}
          render={({ field }) => (
            <FormSelect
              options={[
                ...nigerianStates,
                { label: 'Others', value: 'others' },
              ]}
              value={field.value}
              onChange={field.onChange}
              placeholder="Select state"
              disabled={disabled}
            />
          )}
        />
      </FormField>

      {selectedState === 'others' && (
        <FormField
          label="Specify State"
          error={errors.customState?.message as string | undefined}
          required
        >
          <Input
            placeholder="Enter your state"
            {...register('customState')}
            disabled={disabled}
            readOnly={disabled}
          />
        </FormField>
      )}

      <FormField
        label="LGA/City"
        error={errors.lga?.message as string | undefined}
        required
      >
        <Controller
          name="lga"
          control={control}
          render={({ field }) => (
            <FormSelect
              options={[...cities, { label: 'Others', value: 'others' }]}
              value={field.value}
              onChange={field.onChange}
              placeholder="Select LGA/City"
              disabled={disabled}
            />
          )}
        />
      </FormField>

      {selectedLga === 'others' && (
        <FormField
          label="Specify LGA/City"
          error={errors.customLga?.message as string | undefined}
          required
        >
          <Input
            placeholder="Enter your LGA/City"
            {...register('customLga')}
            disabled={disabled}
            readOnly={disabled}
          />
        </FormField>
      )}

      <FormField
        label="Country"
        error={errors.country?.message as string | undefined}
        required
      >
        <Controller
          name="country"
          control={control}
          render={({ field }) => (
            <FormSelect
              options={[...countries, { label: 'Others', value: 'others' }]}
              value={field.value}
              onChange={field.onChange}
              placeholder="Nigeria"
              disabled={disabled}
            />
          )}
        />
      </FormField>

      {selectedCountry === 'others' && (
        <FormField
          label="Specify Country"
          error={errors.customCountry?.message as string | undefined}
          required
        >
          <Input
            placeholder="Enter your country"
            {...register('customCountry')}
            disabled={disabled}
            readOnly={disabled}
          />
        </FormField>
      )}
    </div>
  );
}
